import React, {useEffect, useState} from 'react';
import {Button as NButton} from "@nextui-org/react";
import {collection, doc, getDoc, getDocs, getFirestore, query, where} from "firebase/firestore";
import {getFunctions, httpsCallable} from "firebase/functions";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";

const db = getFirestore(Firebase);
const adminRemindSeller = httpsCallable(getFunctions(Firebase), 'adminRemindSeller');

// Matches adminRemindSeller: 306 (2 SMS parts) minus the 9-character "Shopped: " prefix.
const MAX_LENGTH = 297;
const SMS_PREFIX = 'Shopped: ';

// GSM-7: basic characters count 1, extension characters 2. Anything else
// (emoji, curly quotes, most accents) sends the whole SMS as UCS-2.
const GSM_BASIC = new Set(Array.from('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'));
const GSM_EXTENDED = new Set(Array.from('^{}\\[~]|€\f'));

/** How many SMS parts the seller's phone receives for this message (prefix included). */
function smsParts(message: string): {parts: number; unicode: boolean; offending: string[]} {
    const text = SMS_PREFIX + message;
    const chars = Array.from(text);
    const offending = Array.from(new Set(chars.filter(c => !GSM_BASIC.has(c) && !GSM_EXTENDED.has(c))));
    if (offending.length > 0) {
        const units = text.length; // UTF-16 code units; an emoji counts 2
        return {parts: units <= 70 ? 1 : Math.ceil(units / 67), unicode: true, offending};
    }
    const units = chars.reduce((n, c) => n + (GSM_EXTENDED.has(c) ? 2 : 1), 0);
    return {parts: units <= 160 ? 1 : Math.ceil(units / 153), unicode: false, offending};
}

// notificationLog event → what the admin reads.
const EVENT_LABELS: Record<string, string> = {
    new_order: 'New order',
    admin_reminder: 'Admin reminder',
    unshipped_warning: 'Ship-soon warning',
};

interface LogRow {event: string; channel: 'push' | 'sms' | 'email'; ok: boolean; to?: string | null; error?: string | null; createdAt?: any}
const CLOSED = ['cancelled', 'completed'];
const PAID = ['successful', 'paid', 'overpaid'];

interface ReminderResult {
    smsSent: number; smsFailed: number; phones: string[];
    emailSent: boolean; email: string | null; pushSent: boolean; message: string;
}

/**
 * "Remind seller" (order drawer → Actions): sends the seller a nudge about
 * this order by push, SMS to every real number and email if they have one —
 * the default wording, or a custom message. Server: adminRemindSeller
 * (one reminder per order per 30 min; logged to adminAuditLog).
 */
export const RemindSeller = ({order}: {order: any}) => {
    const toast = useToast();
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<ReminderResult | null>(null);
    const [last, setLast] = useState<any>(null);
    const [history, setHistory] = useState<LogRow[]>([]);

    const status = (order.orderStatus ?? '').toLowerCase();
    const closed = CLOSED.includes(status);
    const paid = PAID.includes((order.paymentStatus ?? '').toLowerCase());
    const sms = smsParts(message.trim());

    useEffect(() => {
        setResult(null);
        setMessage('');
        setLast(null);
        if (!order?.orderId) return;
        getDoc(doc(db, 'orderReminders', order.orderId))
            .then((snap) => setLast(snap.exists() ? snap.data() : null))
            .catch(() => setLast(null));
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.orderId]);

    const loadHistory = () => {
        if (!order?.orderId) return;
        getDocs(query(collection(db, 'notificationLog'), where('orderId', '==', order.orderId)))
            .then((snap) => setHistory(snap.docs.map(d => d.data() as LogRow)))
            .catch(() => setHistory([]));
    };

    const send = async () => {
        setSending(true);
        try {
            const res = await adminRemindSeller({orderId: order.orderId, message: message.trim()});
            const data = res.data as ReminderResult;
            setResult(data);
            setLast({lastSentAt: {toDate: () => new Date()}, lastResult: data, lastMessage: data.message});
            setMessage('');
            loadHistory();
            toast({position: 'top', title: 'Reminder sent', status: 'success', duration: 2500, isClosable: true});
        } catch (e: any) {
            toast({position: 'top', title: e.message ?? 'Could not send the reminder', status: 'error', duration: 4000, isClosable: true});
        } finally {
            setSending(false);
        }
    };

    const box = {background: '#fff', borderRadius: 8, padding: '12px 16px', border: '1px solid #eee', marginBottom: 18};

    // One line per event (oldest first): "New order · 3 Nov 10:14 — Push ✓ · SMS 1/2 · Email ✓"
    const groups = Object.values(history.reduce((acc: Record<string, {event: string; at: number; rows: LogRow[]}>, row) => {
        const at = row.createdAt?.toMillis?.() ?? 0;
        // Rows of one send land within seconds of each other; a later send of
        // the same event (e.g. a second reminder) starts a new group.
        const key = `${row.event}_${Math.floor(at / 120000)}`;
        if (!acc[key]) acc[key] = {event: row.event, at, rows: []};
        acc[key].rows.push(row);
        return acc;
    }, {})).sort((a, b) => a.at - b.at);

    const channelSummary = (rows: LogRow[], channel: LogRow['channel'], label: string) => {
        const of = rows.filter(r => r.channel === channel);
        if (of.length === 0) return null;
        const ok = of.filter(r => r.ok).length;
        const failed = of.filter(r => !r.ok);
        const text = of.length === 1 ? `${label} ${ok ? '✓' : '✗'}` : `${label} ${ok}/${of.length}`;
        const why = failed.map(r => [r.to, r.error].filter(Boolean).join(': ')).filter(Boolean).join('; ');
        return <span key={channel} title={why || undefined} style={{color: failed.length ? '#b91c1c' : '#15803d'}}>{text}</span>;
    };

    const historyBlock = (
        <div style={{...box, fontSize: 12}}>
            <div style={{fontWeight: 600, color: '#333', marginBottom: 6}}>Notifications sent for this order</div>
            {groups.length === 0 ? (
                <div style={{color: '#888'}}>None recorded yet (tracking started Nov 2026 — older orders won't show any).</div>
            ) : groups.map((g, i) => (
                <div key={i} style={{display: 'flex', flexWrap: 'wrap', gap: 8, padding: '3px 0'}}>
                    <span style={{color: '#333', minWidth: 120}}>
                        {EVENT_LABELS[g.event] ?? g.event.replace(/_/g, ' ')}
                        {g.at ? <span style={{color: '#aaa'}}> · {moment(g.at).format('D MMM HH:mm')}</span> : null}
                    </span>
                    {[channelSummary(g.rows, 'push', 'Push'), channelSummary(g.rows, 'sms', 'SMS'), channelSummary(g.rows, 'email', 'Email')]
                        .filter(Boolean)
                        .reduce((acc: React.ReactNode[], el, j) => (j ? [...acc, <span key={`s${j}`} style={{color: '#ccc'}}>·</span>, el] : [el]), [])}
                </div>
            ))}
            {groups.some(g => g.rows.some(r => !r.ok)) && (
                <div style={{color: '#888', marginTop: 4}}>Hover a red count to see why it failed.</div>
            )}
        </div>
    );

    return (
        <>
            {historyBlock}
            <div style={{fontWeight: 600, marginBottom: 6, color: '#333'}}>Remind seller</div>
            {closed ? (
                <div style={{...box, fontSize: 12, color: '#888'}}>This order is {order.orderStatus} — no reminder needed.</div>
            ) : !paid ? (
                <div style={{...box, fontSize: 12, color: '#888'}}>The buyer hasn't paid yet, so there's nothing to remind the seller about.</div>
            ) : (
                <div style={box}>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 8}}>
                        Sends a push, an SMS to every number the seller has, and an email if they have one.
                        Leave the message empty to send the standard reminder.
                    </div>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                        rows={3}
                        placeholder={status === 'in_transit'
                            ? 'Optional — e.g. "The buyer says it hasn\'t arrived yet. Please update them."'
                            : 'Optional — e.g. "The buyer is asking about this order. Please send it today."'}
                        style={{width: '100%', border: '1px solid #ddd', borderRadius: 6, padding: '8px 10px',
                                fontSize: 13, resize: 'vertical', fontFamily: 'inherit'}}
                    />
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, gap: 8}}>
                        <span style={{fontSize: 11, color: sms.parts > 2 ? '#b91c1c' : sms.parts > 1 ? '#d97706' : '#aaa'}}>
                            {message.length}/{MAX_LENGTH}
                            {message.trim() ? ` · ${sms.parts} SMS part${sms.parts === 1 ? '' : 's'} per number` : ''}
                            {sms.unicode && message.trim()
                                ? ` · ${sms.offending.slice(0, 5).join(' ')} make${sms.offending.length === 1 ? 's' : ''} the SMS longer, remove to save cost`
                                : ''}
                        </span>
                        <NButton size="sm" color="primary" disabled={sending} onPress={send}>
                            {sending ? 'Sending…' : message.trim() ? 'Send custom reminder' : 'Send reminder'}
                        </NButton>
                    </div>

                    {result && (
                        <div style={{marginTop: 10, fontSize: 12, color: '#333', background: '#f7f8fc', borderRadius: 6, padding: '8px 10px'}}>
                            <div>Push: {result.pushSent ? '✓ sent' : '— no device'}</div>
                            <div>
                                SMS: {result.phones.length === 0 ? '— no phone on file'
                                    : `${result.smsSent} sent${result.smsFailed ? `, ${result.smsFailed} failed` : ''} (${result.phones.join(', ')})`}
                            </div>
                            <div>Email: {result.email ? (result.emailSent ? `✓ sent (${result.email})` : `failed (${result.email})`) : '— no email on file'}</div>
                        </div>
                    )}

                    {last?.lastSentAt && !result && (
                        <div style={{marginTop: 10, fontSize: 12, color: '#888'}}>
                            Last reminded {moment(last.lastSentAt.toDate()).fromNow()}
                            {last.lastSentBy ? ` by ${last.lastSentBy}` : ''}
                            {last.count ? ` · ${last.count} reminder${last.count === 1 ? '' : 's'} so far` : ''}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
